import { Attributes, List, Misc, ModelBase, Rules, Validation } from '@singularsystems/neo-core';
import { AppService, DomainTypes } from '../../DomainTypes';
import { isMandatory, MandatoryAndVisibleSetting } from '../Config/FieldConfig/FieldSetting.enum';
import IssuerInvestmentFieldConfig from '../Config/FieldConfig/IssuerInvestmentFieldConfig';
import { IInvestmentCommonFields, IInvestmentHost } from '../Investment/IInvestmentHost';
import Investment from '../Investment/Investment';
import InvestmentInterest from './InvestmentInterest';
import INoteListHost from '../Notes/INoteListHost';
import Note from '../Notes/Note';
import TemporalEntity from '../TemporalEntity';
import NewIssuerInvestmentCommand from './Commands/NewIssuerInvestmentCommand';
import IssuerInvestmentDetail from './IssuerInvestmentDetail';
import IssuerInvestmentPipeline from './IssuerInvestmentPipeline';
import ParticipationAgreementLookup from './Lookups/ParticipationAgreementLookup';
import AuthorisationValues from '../ValueObjects/AuthorisationValues';
import QPTracking from './QPTracking';
import IssuerInvestmentTermsAcceptedDate from './IssuerInvestmentTermsAcceptedDate';

export default class IssuerInvestment extends TemporalEntity implements INoteListHost, IInvestmentHost {
    static typeName = "IssuerInvestment";

    constructor() {
        super();

        this.makeObservable();
    }

    public issuerInvestmentId: number = 0;

    @Attributes.Serialisation(Misc.SerialiseType.NotNull)
    public investmentId: number | null = null;

    @Attributes.ChildObject(Investment)
    public investment: Investment = new Investment();

    @Attributes.Nullable()
    public investorInvestmentId: number | null = null;

    @Attributes.Serialisation(Misc.SerialiseType.NotNull)
    @Rules.Required()
    public entityId: number | null = null;

    @Attributes.Serialisation(Misc.SerialiseType.NotNull)
    @Rules.Required()
    public issuerId: number | null = null;

    @Attributes.Date(Misc.TimeZoneFormat.None)
    public lastAllocationDate: Date | null = null;

    @Attributes.ChildObject(IssuerInvestmentDetail, true)
    public issuerInvestmentDetail: IssuerInvestmentDetail = new IssuerInvestmentDetail();

    @Attributes.NoTracking(Misc.SerialiseType.FullOnly)
    @Attributes.ChildObject(() => LookupFields)
    public lookup: LookupFields = new LookupFields();

    public notes = new List(Note);

    @Attributes.ChildList(InvestmentInterest)
    public investmentInterestList = new List(InvestmentInterest);

    @Attributes.ChildObject(AuthorisationValues, true)
    public authorisation: AuthorisationValues = new AuthorisationValues();

    public isPendingDelete: boolean = false;

    @Attributes.ChildList(IssuerInvestmentPipeline)
    public issuerInvestmentPipelines = new List(IssuerInvestmentPipeline);

    @Attributes.ChildList(QPTracking)
    public qpTracking = new List(QPTracking);

    @Attributes.ChildList(IssuerInvestmentTermsAcceptedDate)
    public termsAcceptedDates = new List(IssuerInvestmentTermsAcceptedDate);

    // Client only properties / methods

    public get investorInvestmentName() {
        return `(${this.investorInvestmentId}) ${this.lookup.investorInvestmentName}`;
    }

    public get commonFields(): IInvestmentCommonFields {
        return {
            lastAllocationDate: this.lastAllocationDate,
            productTypeId: this.issuerInvestmentDetail.productTypeId,
            isCeded: false,
        }
    }

    public setIdOnNote(note: Note): void {
        note.issuerInvestmentId = this.issuerInvestmentId;
    }

    public static fromCreateCommand(command: NewIssuerInvestmentCommand) {
        const issuerInvestment = new IssuerInvestment();
        issuerInvestment.issuerId = command.issuerId;
        issuerInvestment.entityId = command.entityId;
        issuerInvestment.investorInvestmentId = command.investorInvestmentId;
        issuerInvestment.lookup = command.lookup;

        return issuerInvestment;
    }

    protected addBusinessRules(rules: Validation.Rules<this>) {
        super.addBusinessRules(rules);

        // Rules affected by configurable mandatory settings
        rules.failWhen(c => !c.investment.initialEndDate && this.isRequired("initialEndDate"), "Initial end date is required.");
        rules.failWhen(c => !c.investment.compoundingCycleId && this.isRequired("compoundingCycleId"), "Compounding cycle is required.");
    }

    public toString(): string {
        if (this.isNew) {
            return "New issuer investment";
        } else {
            return "Issuer Investment";
        }
    }

    private isRequired(fieldName: keyof IssuerInvestmentFieldConfig) {
        return isMandatory(AppService.get(DomainTypes.Services.DataCache).fieldConfig.issuerInvestmentFields[fieldName] as MandatoryAndVisibleSetting);
    }
}

export class LookupFields extends ModelBase {
    static typeName = "LookupFields";

    constructor() {
        super();
        this.makeObservable();
    }

    public issuerName: string | null = null;

    public investorInvestmentName: string | null = null;

    public hasVestingTrust: boolean = false;

    public isIssuerAuthorised: boolean = false;

    public isIssuerRestricted: boolean = false;

    public participationAgreements = new List(ParticipationAgreementLookup);

    // Client only properties / methods
}